import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, Check, X, Star, Send, MessageSquare, Search, Lock, FileText, Users } from "lucide-react";
import { api, fmtErr } from "../../../lib/api";
import { limitToast } from "../../../lib/limits";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Badge } from "../../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../../components/ui/dialog";
import FileUploader from "../../../components/FileUploader";
import { VatBreakdown } from "./components";
import { toast } from "sonner";

const STATUS = {
  invited: "bg-zinc-100 text-zinc-600",
  quoted: "bg-sky-50 text-sky-700",
  accepted: "bg-emerald-50 text-emerald-700",
  declined: "bg-amber-50 text-amber-700",
  rejected: "bg-rose-50 text-rose-700",
};
const DONE = ["completed", "done", "confirmed"];

function QuoteRow({ q, jobId, canManage, locked, onChange, autoOpenMsg = false }) {
  const [showMsg, setShowMsg] = useState(autoOpenMsg);
  const [msg, setMsg] = useState("");
  const [quoteForm, setQuoteForm] = useState({ currency: "AED", vat: "", items: [{ text: "", price: "" }] });
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [counterForm, setCounterForm] = useState({ amount: "" });
  const [showCounter, setShowCounter] = useState(false);
  const [busy, setBusy] = useState(false);
  const threadRef = useRef(null);
  useEffect(() => {
    if (!showMsg) return;
    requestAnimationFrame(() => {
      if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
    });
  }, [showMsg, (q.messages || []).length]);
  // Auto-opened via a "quote received / new messages" badge — clear unread.
  useEffect(() => {
    if (autoOpenMsg && (q.unread || 0) > 0) {
      api.post(`/quotes/${q.id}/mark-read`).then(onChange).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const act = async (fn, escalate = null) => { setBusy(true); try { await fn(); onChange(); } catch (e) { if (!limitToast(e, escalate)) toast.error(fmtErr(e)); } finally { setBusy(false); } };
  // Enter quote on behalf of an offline supplier — same shape as a supplier's
  // own quote: scope line by line with a price behind, auto-calculated total.
  const quoteSubtotal = quoteForm.items.reduce((s, it) => s + (parseFloat(it.price) || 0), 0);
  const quoteVatPct = quoteForm.vat === "" ? null : Number(quoteForm.vat);
  const quoteVatAmt = quoteVatPct ? Math.round(quoteSubtotal * quoteVatPct) / 100 : 0;
  const quoteTotal = Math.round((quoteSubtotal + quoteVatAmt) * 100) / 100;
  const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const setItem = (i, patch) => setQuoteForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  const addItem = () => setQuoteForm((f) => ({ ...f, items: [...f.items, { text: "", price: "" }] }));
  const removeItem = (i) => setQuoteForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const submitQuote = () => act(async () => {
    const items = quoteForm.items.filter((it) => it.text.trim());
    if (!items.length) { toast.error("Add at least one scope line"); throw new Error("scope"); }
    if (quoteTotal <= 0) { toast.error("Add a price to at least one scope line"); throw new Error("total"); }
    await api.post(`/quotes/${q.id}/submit`, {
      amount: quoteTotal,
      currency: quoteForm.currency,
      vat_percent: quoteVatPct,
      scope_items: items.map((it) => ({ text: it.text.trim(), price: it.price === "" ? null : Number(it.price) })),
    });
    setShowQuoteForm(false); toast.success(q.status === "invited" ? "Quote recorded" : "Quote updated");
  });
  const addQuoteDoc = (path, name) => act(async () => {
    await api.post(`/quotes/${q.id}/documents`, { file_path: path, name, kind: "quotation" });
    toast.success("Quotation attached — also visible in the task's Documents");
  });
  // Offline supplier (or tracked offline): the yacht maintains the quote itself.
  const isOffline = q.supplier_kind !== "marketplace" || !!q.track_offline;
  const canEnterQuote = canManage && (
    (!locked && q.status === "invited") ||
    (isOffline && (q.status === "quoted" || q.status === "accepted"))
  );
  const openQuoteForm = () => setShowQuoteForm((v) => {
    if (!v && q.status !== "invited") {
      setQuoteForm({
        currency: q.currency || "AED",
        vat: q.vat_percent != null ? String(q.vat_percent) : "",
        items: (q.scope_items || []).length
          ? q.scope_items.map((s) => ({ text: s.text || "", price: s.price != null ? String(s.price) : "" }))
          : [{ text: "", price: "" }],
      });
    }
    return !v;
  });
  const accept = () => act(
    async () => { await api.post(`/quotes/${q.id}/accept`); toast.success("Quote accepted"); },
    { yacht_id: q.yacht_id, kind: "quote", ref_id: jobId, action_id: q.id,
      label: q.supplier_name || "", amount: q.status === "negotiating" ? q.current_amount : q.amount },
  );
  const decline = () => act(async () => { await api.post(`/quotes/${q.id}/decline`); });
  const counter = () => act(async () => {
    if (!counterForm.amount) { toast.error("Enter a counter amount"); throw new Error("amount"); }
    await api.post(`/quotes/${q.id}/counter`, { amount: Number(counterForm.amount), currency: q.current_currency || q.currency || "EUR" });
    setShowCounter(false); setCounterForm({ amount: "" }); toast.success("Counter-offer sent");
  });
  const send = () => act(async () => { if (!msg.trim()) return; await api.post(`/quotes/${q.id}/messages`, { text: msg.trim() }); setMsg(""); });
  // Unresponsive supplier (48h without a reaction) → owner can flag the quote
  // "tracked offline": no more notifications to this supplier for THIS task.
  const invitedOver48h = q.invited_at && (Date.now() - new Date(q.invited_at).getTime()) >= 48 * 3600 * 1000;
  const trackOffline = () => act(async () => {
    await api.post(`/quotes/${q.id}/track-offline`);
    toast.success("Tracked offline — no more notifications to this supplier for this task");
  });
  // Sender-side undo: remove own message within 15 min, never once accepted.
  const canRemoveMsg = (m) => !m.deleted && q.status !== "accepted" && Date.now() - new Date(m.at).getTime() < 15 * 60 * 1000;
  const removeMsg = (mid) => act(async () => { await api.delete(`/quotes/${q.id}/messages/${mid}`); });
  const fileUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
  const canDecide = canManage && !locked && (q.status === "quoted" || (q.status === "negotiating" && q.awaiting === "requester"));
  // A freshly received quote / counter that now needs the crew's decision.
  const isNewForCrew = !locked && q.awaiting === "requester" && (q.status === "quoted" || q.status === "negotiating");

  const unread = q.unread || 0;
  const toggleMsg = async () => {
    const next = !showMsg;
    setShowMsg(next);
    if (next && unread > 0) {
      try { await api.post(`/quotes/${q.id}/mark-read`); onChange(); } catch { /* non-fatal */ }
    }
  };
  const fmtTime = (s) => {
    if (!s) return "";
    try { return new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };

  return (
    <div className={`rounded-xl border overflow-hidden transition-colors ${isNewForCrew ? "border-sky-300 ring-1 ring-sky-200" : "border-indigo-200"}`} data-testid={`quote-${q.id}`}>
      {/* Supplier header band — tinted so each supplier card reads as its own section */}
      <div className={`flex items-center gap-2 flex-wrap px-3 py-2 border-b ${isNewForCrew ? "bg-sky-50 border-sky-100" : "bg-indigo-50/70 border-indigo-100"}`}>
        <span className="font-semibold text-sm text-zinc-900">{q.supplier_name}</span>
        <Badge variant="secondary" className="text-[10px] bg-white border border-zinc-200">{q.supplier_kind === "marketplace" ? "Online" : "Offline"}</Badge>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[q.status] || ""}`} data-testid={`quote-status-${q.id}`}>{q.status}</span>
        {q.track_offline && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-600" title="No notifications go to this supplier for this task — your crew handles it on their behalf." data-testid={`quote-track-offline-${q.id}`}>
            Tracked offline
          </span>
        )}
        {isNewForCrew && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-600 text-white animate-pulse" data-testid={`quote-new-${q.id}`}>
            New quote
          </span>
        )}
        {q.amount != null && <span className="ml-auto text-sm font-semibold text-emerald-700">{q.currency} {q.amount}{q.start_date ? ` · work starts ${q.start_date}` : q.eta_days ? ` · ${q.eta_days}d` : ""}{q.end_date ? ` → ${q.end_date}` : ""}</span>}
      </div>
      <div className="p-3">
      {q.amount != null && q.current_amount != null && q.current_amount !== q.amount && (
        <p className="text-[11px] text-zinc-500 mt-1">Current offer: <span className="font-medium text-zinc-800">{q.current_currency || q.currency} {q.current_amount}</span>{q.awaiting === "requester" ? " · awaiting your response" : q.awaiting === "supplier" ? " · awaiting supplier" : ""}</p>
      )}
      {(q.scope_items || []).length > 0 && (
        <div className="mt-1.5 rounded-md border border-zinc-100 bg-zinc-50/60 p-2" data-testid={`quote-scope-${q.id}`}>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Scope</div>
          <ul className="space-y-0.5">
            {q.scope_items.map((s, i) => (
              <li key={i} className="flex items-start justify-between gap-3 text-xs text-zinc-700">
                <span className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]"><span className="text-zinc-400 font-medium mr-1">{i + 1}.</span>{s.text}</span>
                {s.price != null && <span className="text-zinc-500 whitespace-nowrap shrink-0">{q.currency} {s.price}</span>}
              </li>
            ))}
          </ul>
          <VatBreakdown items={q.scope_items} amount={q.amount} currency={q.currency} vatPercent={q.vat_percent} testid={`quote-vat-view-${q.id}`} />
          {q.vat_percent != null && (
            <div className="mt-1 pt-1 border-t border-zinc-200 flex items-baseline justify-between text-xs">
              <span className="text-zinc-500">Total incl. VAT</span>
              <span className="font-medium text-zinc-800">{q.currency} {q.amount}</span>
            </div>
          )}
        </div>
      )}
      {q.notes && <p className="text-xs text-zinc-500 mt-1">{q.notes}</p>}

      {(q.quote_documents || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`quote-docs-${q.id}`}>
          {q.quote_documents.map((d) => (
            <a key={d.id} href={fileUrl(d.file_path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 max-w-full text-[11px] text-sky-700 border border-sky-200 bg-sky-50 rounded px-2 py-0.5 hover:bg-sky-100" data-testid={`quote-doc-${d.id}`}>
              <FileText className="h-3 w-3 shrink-0" /> <span className="truncate" title={d.name}>{d.name || "Quotation"}</span>
            </a>
          ))}
        </div>
      )}

      {(q.offers || []).length > 0 && (
        <div className="mt-2 border-l-2 border-zinc-100 pl-2 space-y-0.5" data-testid={`quote-offers-${q.id}`}>
          {q.offers.map((o) => (
            <div key={o.id} className="text-[11px] text-zinc-500">
              <span className="font-medium text-zinc-700">{o.by === "supplier" ? "Supplier" : "Yacht"}</span>: {o.currency} {o.amount}{o.note ? ` — ${o.note}` : ""}
            </div>
          ))}
        </div>
      )}

      {q.counter_declined && q.status === "quoted" && !locked && (
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800" data-testid={`counter-declined-${q.id}`}>
          The supplier declined your counter offer — their last proposal of{" "}
          <strong>{q.current_currency || q.currency} {q.current_amount}</strong> stands.
          Accept it to approve the task, or decline the quote.
        </div>
      )}

      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {canEnterQuote && (
          <Button size="sm" variant="outline" onClick={openQuoteForm} data-testid={`enter-quote-${q.id}`}>{q.status === "invited" ? "Enter quote" : "Edit quote"}</Button>
        )}
        {canManage && !locked && q.status === "invited" && !q.track_offline && invitedOver48h && (
          <Button
            size="sm" variant="outline"
            className="text-zinc-600 border-zinc-300"
            onClick={trackOffline} disabled={busy}
            title="The supplier hasn't reacted for 48 hours — stop all further notifications to them for this task."
            data-testid={`track-offline-${q.id}`}
          >Track offline</Button>
        )}
        {canDecide && (
          <>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={accept} disabled={busy} data-testid={`accept-quote-${q.id}`}><Check className="h-3.5 w-3.5 mr-1" />Accept{q.current_amount != null ? ` ${q.current_currency || q.currency} ${q.current_amount}` : ""}</Button>
            {!q.counter_declined && !isOffline && (
              <Button size="sm" variant="outline" onClick={() => setShowCounter((v) => !v)} disabled={busy} data-testid={`counter-quote-${q.id}`}>Counter</Button>
            )}
            <Button size="sm" variant="outline" onClick={decline} disabled={busy}><X className="h-3.5 w-3.5 mr-1" />Decline</Button>
          </>
        )}
        {!isOffline && (
        <button onClick={toggleMsg} className="text-xs font-medium text-emerald-700 inline-flex items-center gap-1.5 ml-auto rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 hover:bg-emerald-100 transition-colors" data-testid={`msg-toggle-${q.id}`}>
          <MessageSquare className="h-3.5 w-3.5" />
          Messages
          {(q.messages || []).length > 0 && <span className="text-emerald-600/70">({(q.messages || []).length})</span>}
          {unread > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1 rounded-full text-[10px] font-semibold bg-red-600 text-white" data-testid={`msg-unread-${q.id}`}>{unread}</span>
          )}
        </button>
        )}
      </div>

      {showQuoteForm && (
        <div className="mt-2 space-y-2 rounded-md border border-zinc-200 bg-zinc-50/60 p-2.5" data-testid={`quote-form-${q.id}`}>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Scope — one line per step, price behind</div>
          {quoteForm.items.map((it, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-xs text-zinc-400 w-4 shrink-0 pt-2">{i + 1}.</span>
              <Textarea placeholder="Scope line (e.g. Replace impeller)" rows={3} className="flex-1 text-sm min-h-0" value={it.text} onChange={(e) => setItem(i, { text: e.target.value })} data-testid={`quote-item-text-${q.id}-${i}`} />
              <Input placeholder="Price" type="number" min="0" className="w-20 shrink-0 no-spinner" value={it.price} onChange={(e) => setItem(i, { price: e.target.value })} data-testid={`quote-item-price-${q.id}-${i}`} />
              {quoteForm.items.length > 1 && (
                <button type="button" onClick={() => removeItem(i)} className="text-zinc-400 hover:text-red-600 shrink-0 pt-2" title="Remove line" data-testid={`quote-item-remove-${q.id}-${i}`}>
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={addItem} data-testid={`quote-add-item-${q.id}`}>+ Add line</Button>
          <div className="flex items-start gap-2 pt-1 border-t border-zinc-200">
            <select
              value={quoteForm.currency}
              onChange={(e) => setQuoteForm((f) => ({ ...f, currency: e.target.value }))}
              className="h-8 rounded-md border border-input px-2 text-sm bg-white"
              data-testid={`quote-currency-${q.id}`}
            >
              <option value="AED">AED</option>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
            </select>
            <div className="flex items-center gap-1">
              <Input placeholder="VAT" type="number" min="0" max="100" className="w-16 h-8 text-sm no-spinner" value={quoteForm.vat} onChange={(e) => setQuoteForm((f) => ({ ...f, vat: e.target.value }))} data-testid={`quote-vat-${q.id}`} />
              <span className="text-xs text-zinc-500">VAT&nbsp;%</span>
            </div>
            <div className="ml-auto text-right space-y-0.5" data-testid={`quote-total-${q.id}`}>
              {quoteVatPct != null && (
                <>
                  <div className="text-[11px] text-zinc-500">Subtotal {quoteForm.currency} {money(quoteSubtotal)}</div>
                  <div className="text-[11px] text-zinc-500">VAT {quoteVatPct}% {quoteForm.currency} {money(quoteVatAmt)}</div>
                </>
              )}
              <div className="text-sm text-zinc-600">
                Total{quoteVatPct != null ? " incl. VAT" : ""} <span className="font-semibold text-zinc-900">{quoteForm.currency} {money(quoteTotal)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FileUploader yacht_id={q.yacht_id} folder="quotes" accept="application/pdf,image/*" multiple label="Attach quotation" testid={`quote-attach-${q.id}`} onUploaded={(path, record) => addQuoteDoc(path, record?.original_filename || "Quotation")} />
          </div>
          <Button size="sm" className="w-full bg-ink hover:bg-black" onClick={submitQuote} disabled={busy} data-testid={`save-quote-${q.id}`}>Save quote</Button>
        </div>
      )}

      {showCounter && (
        <div className="mt-2 grid grid-cols-[1fr_auto] gap-2" data-testid={`counter-form-${q.id}`}>
          <Input placeholder={`Counter amount (${q.current_currency || q.currency || "EUR"})`} type="number" value={counterForm.amount} onChange={(e) => setCounterForm((f) => ({ ...f, amount: e.target.value }))} data-testid={`counter-amount-${q.id}`} />
          <Button size="sm" className="bg-ink hover:bg-black" onClick={counter} disabled={busy} data-testid={`send-counter-${q.id}`}>Send counter</Button>
        </div>
      )}
      </div>

      {showMsg && !isOffline && (
        <div className="border-t border-emerald-100 bg-emerald-50/40 px-3 py-3" data-testid={`quote-msg-section-${q.id}`}>
          <div ref={threadRef} className="space-y-2.5 max-h-64 overflow-y-auto pr-1" data-testid={`quote-thread-${q.id}`}>
            {(q.messages || []).length === 0 && (
              <p className="text-xs text-zinc-400 text-center py-3">No messages yet — start the conversation with the supplier.</p>
            )}
            {(q.messages || []).map((m) => {
              const mine = m.sender_kind === "requester";
              return (
                <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs ${mine ? "bg-ink text-white rounded-br-sm" : "bg-white border border-emerald-100 text-zinc-800 rounded-bl-sm"}`}>
                    <div className={`text-[10px] mb-0.5 font-medium ${mine ? "text-zinc-300" : "text-zinc-500"}`}>{m.sender_name}</div>
                    {m.deleted
                      ? <div className="italic opacity-60" data-testid={`msg-removed-${m.id}`}>Message removed</div>
                      : <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                  </div>
                  <div className="text-[10px] text-zinc-400 mt-0.5 px-1 flex items-center gap-2">
                    {fmtTime(m.at)}
                    {mine && canRemoveMsg(m) && (
                      <button type="button" onClick={() => removeMsg(m.id)} className="text-zinc-400 hover:text-red-600 underline underline-offset-2" title="Remove this message (within 15 minutes of sending)" data-testid={`msg-remove-${m.id}`}>Remove</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2 mt-2.5">
            <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Write a message…" className="bg-white" onKeyDown={(e) => e.key === "Enter" && send()} data-testid={`msg-input-${q.id}`} />
            <Button size="icon" className="bg-ink hover:bg-black shrink-0" onClick={send} disabled={busy} data-testid={`msg-send-${q.id}`}><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewDialog({ jobId, supplierId, onClose, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/marketplace/suppliers/${supplierId}/reviews`, { task_id: jobId, rating, comment }); toast.success("Review submitted"); onDone(); onClose(); }
    catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="review-dialog">
        <DialogHeader><DialogTitle>Review supplier</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Rate the supplier for this completed task</DialogDescription>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
              <Star className={`h-7 w-7 ${n <= rating ? "fill-amber-400 stroke-amber-400" : "stroke-zinc-300"}`} />
            </button>
          ))}
        </div>
        <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="How was the service?" rows={3} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-ink hover:bg-black" data-testid="review-submit">Submit review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TaskQuotes({ jobId, jobStatus, canManage, onFindSupplier, autoExpand = false }) {
  const [data, setData] = useState(null);
  const [review, setReview] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/maintenance/${jobId}/quotes`); setData(data); }
    catch (e) { toast.error(fmtErr(e)); }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-8 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  const { quotes = [], locked, assigned_supplier } = data;
  const isDone = DONE.includes(jobStatus);
  const canReview = isDone && locked && assigned_supplier?.supplier_kind === "marketplace" && canManage;

  return (
    <div className="mt-4 space-y-3" data-testid="task-quotes">
      {locked && assigned_supplier ? (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 flex items-center gap-2 text-sm" data-testid="quotes-locked">
          <Lock className="h-4 w-4 text-emerald-700" />
          <span><span className="font-medium">{assigned_supplier.supplier_name}</span> accepted — {assigned_supplier.currency} {assigned_supplier.amount}. No further quotes.</span>
          {canReview && <Button size="sm" className="ml-auto bg-ink hover:bg-black" onClick={() => setReview(assigned_supplier.supplier_id)} data-testid="leave-review-btn"><Star className="h-3.5 w-3.5 mr-1" />Leave review</Button>}
        </div>
      ) : (
        canManage && (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-indigo-100 bg-indigo-50/50 px-3 py-2">
            <span className="text-xs font-semibold text-indigo-800 inline-flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              {quotes.length ? `${quotes.length} supplier${quotes.length > 1 ? "s" : ""} requested to quote` : "Suppliers requested to quote"}
            </span>
            <Button size="sm" onClick={onFindSupplier} className="bg-ink hover:bg-black" data-testid="find-supplier-btn"><Search className="h-4 w-4 mr-1" />Add more suppliers</Button>
          </div>
        )
      )}

      {quotes.length === 0 && !locked && (
        <p className="text-sm text-zinc-500" data-testid="quotes-empty">No suppliers selected yet. Use “Add more suppliers” to choose companies to request a quote from.</p>
      )}
      {quotes.map((q) => (
        <QuoteRow
          key={q.id} q={q} jobId={jobId} canManage={canManage} locked={locked} onChange={load}
          autoOpenMsg={autoExpand}
        />
      ))}

      {review && <ReviewDialog jobId={jobId} supplierId={review} onClose={() => setReview(null)} onDone={load} />}
    </div>
  );
}
